// src/components/RsuVestInputs.js
import React, { useState, useEffect } from 'react';
import { Form, Checkbox, Button, DatePicker, InputNumber, Space, Table, Typography, Tooltip, Card, Row, Col, Modal, Tag, Collapse, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;
const { Panel } = Collapse;

const RsuVestInputs = ({ vestEvents = [], onChange, taxYear, isTaxResidentInScotland, isEmployerNicLiable = true, onEmployerNicLiableChange }) => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [soldImmediately, setSoldImmediately] = useState(true);
  const [editingSoldImmediately, setEditingSoldImmediately] = useState(true);
  const [editingVest, setEditingVest] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  // Show different explanation based on tax year
  const is2025OrLater = taxYear === "2025/2026" || parseInt(taxYear.split('/')[0]) >= 2025;

  // Monitor the sold_immediately value to enable/disable capital gains
  useEffect(() => {
    // Update the form field when soldImmediately changes
    form.setFieldsValue({ sold_immediately: soldImmediately });
  }, [soldImmediately, form]);

  // Handle employer NIC liability change
  const handleEmployerNicLiableChange = (e) => {
    if (onEmployerNicLiableChange) {
      onEmployerNicLiableChange(e.target.checked);
    }
  };

  // Get the default tax rate based on tax year and region
  const getDefaultTaxRate = () => {
    // For Scottish residents, the default rate is typically 41% or 42% depending on the tax year
    // For rest of UK, it's typically 40%
    return isTaxResidentInScotland ? 42 : 40;
  };

  // Add a new vest event
  const handleAdd = () => {
    // Get values from form
    form.validateFields().then(values => {
      const newVest = {
        id: Date.now(), // Unique ID for the event
        date: values.date.format('YYYY-MM-DD'),
        amount: values.amount,
        withholding_rate: values.withholding_rate || 50, // Default to 50% if not specified
        employer_nic_rate: values.employer_nic_rate || 6.9, // Default to 6.9% if not specified
        sold_immediately: values.sold_immediately,
        capital_gains: values.sold_immediately ? 0 : (values.capital_gains || 0),
        // Calculate estimated PAYE for this vest
        estimated_paye: calculateEstimatedPaye(values.amount, values.withholding_rate || 50, getDefaultTaxRate())
      };

      // Add to the list and notify parent
      const newVestEvents = [...vestEvents, newVest];
      onChange(newVestEvents);
      
      // Reset form for next entry
      form.resetFields();
      setSoldImmediately(true);
    });
  };

  // Calculate estimated PAYE for a single vest event
  const calculateEstimatedPaye = (amount, withholdingRate, defaultTaxRate) => {
    // Employer NIC rate is typically around 6.9%
    const employerNicRate = 6.9;
    
    // Calculate amount subject to income tax after employer NIC
    const subjectToTax = amount * (1 - employerNicRate/100);
    
    // Calculate withholding amount (what was actually withheld)
    const withholdingAmount = amount * (withholdingRate/100);
    
    // Calculate what PAYE would typically show (default tax rate)
    const payeAmount = subjectToTax * (defaultTaxRate/100);
    
    // Calculate the difference (additional tax due or refund)
    const difference = payeAmount - withholdingAmount;
    
    return {
      subject_to_tax: subjectToTax,
      withholding_amount: withholdingAmount,
      paye_amount: payeAmount,
      difference: difference
    };
  };

  // Show edit modal for a vest event
  const handleEdit = (vest) => {
    setEditingVest(vest);
    setEditingSoldImmediately(vest.sold_immediately);
    
    // Set form values
    editForm.setFieldsValue({
      date: moment(vest.date),
      amount: vest.amount,
      withholding_rate: vest.withholding_rate,
      employer_nic_rate: vest.employer_nic_rate,
      sold_immediately: vest.sold_immediately,
      capital_gains: vest.capital_gains,
    });
    
    setIsEditModalVisible(true);
  };

  // Save edited vest event
  const handleEditSave = () => {
    editForm.validateFields().then(values => {
      const updatedVest = {
        ...editingVest,
        date: values.date.format('YYYY-MM-DD'),
        amount: values.amount,
        withholding_rate: values.withholding_rate,
        employer_nic_rate: values.employer_nic_rate,
        sold_immediately: values.sold_immediately,
        capital_gains: values.sold_immediately ? 0 : (values.capital_gains || 0),
        // Recalculate estimated PAYE
        estimated_paye: calculateEstimatedPaye(values.amount, values.withholding_rate, getDefaultTaxRate())
      };

      // Update the list and notify parent
      const updatedVestEvents = vestEvents.map(vest => 
        vest.id === editingVest.id ? updatedVest : vest
      );
      
      onChange(updatedVestEvents);
      
      // Close modal and reset
      setIsEditModalVisible(false);
      setEditingVest(null);
    });
  };

  // Cancel editing
  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingVest(null);
  };

  // Handle sold immediately checkbox change for edit form
  const handleEditSoldImmediatelyChange = (e) => {
    setEditingSoldImmediately(e.target.checked);
    
    // If checked, clear capital gains
    if (e.target.checked) {
      editForm.setFieldsValue({ capital_gains: 0 });
    }
  };

  // Remove a vest event
  const handleDelete = (id) => {
    const newVestEvents = vestEvents.filter(vest => vest.id !== id);
    onChange(newVestEvents);
  };

  // Handle sold immediately checkbox change
  const handleSoldImmediatelyChange = (e) => {
    setSoldImmediately(e.target.checked);
    
    // If checked, clear capital gains
    if (e.target.checked) {
      form.setFieldsValue({ capital_gains: 0 });
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Calculate annual totals
  const totalRsuAmount = vestEvents.reduce((sum, vest) => sum + vest.amount, 0);
  const totalWithholding = vestEvents.reduce((sum, vest) => 
    sum + (vest.amount * vest.withholding_rate / 100), 0);
  const totalCapitalGains = vestEvents.reduce((sum, vest) => 
    sum + (vest.sold_immediately ? 0 : vest.capital_gains), 0);

  // Table columns definition
  const columns = [
    {
      title: 'Vest Date',
      dataIndex: 'date',
      key: 'date',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'Amount (£)',
      dataIndex: 'amount',
      key: 'amount',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Withholding Rate',
      dataIndex: 'withholding_rate',
      key: 'withholding_rate',
      render: (value) => `${value}%`
    },
    {
      title: 'Employer NIC Rate',
      dataIndex: 'employer_nic_rate',
      key: 'employer_nic_rate',
      render: (value) => `${value}%`
    },
    {
      title: 'Sold Immediately',
      dataIndex: 'sold_immediately',
      key: 'sold_immediately',
      render: (value) => value ? 'Yes' : 'No'
    },
    {
      title: 'Capital Gains (£)',
      dataIndex: 'capital_gains',
      key: 'capital_gains',
      render: (value, record) => record.sold_immediately ? '-' : 
        formatCurrency(value)
    },
    {
      title: 'Est. PAYE (£)',
      key: 'estimated_paye',
      render: (_, record) => {
        if (!record.estimated_paye) return '-';
        
        // For the amount shown on the payslip, we use the paye_amount directly
        const payeAmount = record.estimated_paye.paye_amount;
        const effectiveRate = record.estimated_paye.effective_rate;
        const monthsIntoTaxYear = record.months_into_tax_year;
        const projectedIncome = record.projected_annual_income;
        
        // Get the default tax rate to use when no effective rate is available
        const defaultRate = getDefaultTaxRate();
        
        return (
          <Tooltip title={
            <div>
              <p><strong>Month {monthsIntoTaxYear} of tax year ({record.month_name})</strong></p>
              <p><strong>RSU Value after NIC:</strong> {formatCurrency(record.estimated_paye.subject_to_tax)}</p>
              <p><strong>Withheld at {record.withholding_rate}%:</strong> {formatCurrency(record.estimated_paye.withholding_amount)}</p>
              <p><strong>Estimated PAYE:</strong> {formatCurrency(payeAmount)} <small>({effectiveRate?.toFixed(1) || defaultRate}% rate)</small></p>
              <p><strong>HMRC's Projected Annual Income:</strong> {formatCurrency(projectedIncome)}</p>
              <p><strong>Difference:</strong> {formatCurrency(record.estimated_paye.difference)}</p>
              <p><small><em>HMRC progressively adjusts tax calculations as more income data becomes available throughout the tax year.</em></small></p>
            </div>
          }>
            <span>
              {formatCurrency(payeAmount)}
              {record.estimated_paye.difference > 0 ? 
                <Tag color="red" style={{ marginLeft: 8 }}>+{formatCurrency(record.estimated_paye.difference)}</Tag> : 
                record.estimated_paye.difference < 0 ?
                <Tag color="green" style={{ marginLeft: 8 }}>{formatCurrency(record.estimated_paye.difference)}</Tag> :
                null
              }
              <Tag color="blue" style={{ marginLeft: 4 }}>{record.month_name}</Tag>
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      )
    }
  ];

  // Form item layout to align labels and inputs
  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  return (
    <Card title="RSU Vest Events" style={{ marginBottom: 20 }}>
      <Alert
        type="info"
        message={
          <Space direction="vertical" size="small">
            <Text strong>Employer NIC Liability for RSUs</Text>
            <Checkbox 
              checked={isEmployerNicLiable} 
              onChange={handleEmployerNicLiableChange}
              style={{ marginBottom: 8 }}
            >
              Employee is liable for employer-side NIC on RSUs
              <Tooltip title="If checked, employer NIC (typically 6.9%) will be deducted from RSU value before calculating income tax. Some companies pass this cost to employees, while others don't. From tax year 2025/26, this is typically not applicable.">
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Checkbox>
            
            <Text type="secondary" style={{ display: 'block' }}>
              {is2025OrLater ? 
                "Note: From tax year 2025/26, employees are typically not liable for employer-side NIC on RSUs, but your company policy may vary." :
                "Typically, pre-2025/26 tax years have this enabled as employees were generally liable for employer NIC."}
            </Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
        showIcon
      />      
      <Form form={form} {...formItemLayout}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={4}>
            <Form.Item 
              name="date" 
              label={
                <Space>
                  Vest Date
                  <Tooltip title="The date when your RSUs vested">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Select Date" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={12} md={4}>
            <Form.Item 
              name="amount" 
              label={
                <Space>
                  Amount (£)
                  <Tooltip title="The total value of RSUs that vested on this date">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true, message: 'Enter amount' }]}
            >
              <InputNumber 
                style={{ width: '100%' }}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Form.Item 
              name="withholding_rate" 
              initialValue={50}
              label={
                <Space>
                  Withholding Rate (%)
                  <Tooltip title="The percentage of tax withheld at vest time (typically 50% for higher rate taxpayers)">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber 
                style={{ width: '100%' }}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
                min={0}
                max={100}
                placeholder="50"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Form.Item 
              name="employer_nic_rate" 
              initialValue={6.9}
              label={
                <Space>
                  Employer NIC Rate (%)
                  <Tooltip title="The percentage of employer National Insurance Contributions that are deducted from your RSU amount (typically around 6.9%)">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber 
                style={{ width: '100%' }}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
                min={0}
                max={100}
                placeholder="6.9"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Form.Item 
              name="sold_immediately" 
              valuePropName="checked"
              initialValue={true}
              label={
                <Space>
                  Sold Immediately
                  <Tooltip title="Check this if RSUs were sold immediately upon vesting (no capital gains tax implications)">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              <Checkbox checked={soldImmediately} onChange={handleSoldImmediatelyChange}>
                Sold immediately
              </Checkbox>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Form.Item 
              name="capital_gains" 
              initialValue={0}
              label={
                <Space>
                  Capital Gains (£)
                  <Tooltip title="If RSUs were not sold immediately, enter any capital gains realized when they were eventually sold">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              <InputNumber 
                style={{ width: '100%' }}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
                disabled={soldImmediately}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24} style={{ textAlign: 'right', marginBottom: 16 }}>
            <Button 
              type="primary" 
              onClick={handleAdd} 
              icon={<PlusOutlined />}
            >
              Add Vest Event
            </Button>
          </Col>
        </Row>
      </Form>

      {vestEvents.length > 0 && (
        <>
          <Table 
            dataSource={vestEvents} 
            columns={columns} 
            rowKey="id" 
            pagination={false}
            style={{ marginTop: 16, marginBottom: 16 }}
            size="small"
          />
          
          <Card size="small" title="Annual Totals">
            <Text strong>Total RSU Amount:</Text> {formatCurrency(totalRsuAmount)} <br />
            <Text strong>Total Tax Withheld:</Text> {formatCurrency(totalWithholding)} <br />
            <Text strong>Total Capital Gains:</Text> {formatCurrency(totalCapitalGains)}
          </Card>
          
          <div style={{ marginTop: 20 }}>
            <Collapse defaultActiveKey={[]} ghost>
              <Panel 
                header={
                  <span style={{ fontWeight: 'bold' }}>Understanding PAYE Calculations for RSUs</span>
                } 
                key="1"
              >
                <Card size="small" bordered={false} style={{ background: '#f9f9f9' }}>
                  <p>
                    <strong>Each vest event shows an estimated PAYE value.</strong> This represents what would typically appear on your payslip for that month's RSU vest.
                  </p>
                  <ul>
                    <li>The standard PAYE calculation applies the default tax rate ({getDefaultTaxRate()}%) to your RSU income after employer NICs.</li>
                    <li>A <Tag color="red">red tag</Tag> indicates additional tax may be due at year-end.</li>
                    <li>A <Tag color="green">green tag</Tag> indicates a potential refund at year-end.</li>
                    <li>These per-vest estimates help explain why your monthly payslips may show different tax amounts when RSUs vest.</li>
                  </ul>
                  <p>Note: The final tax adjustment is calculated based on your total annual income and settled through Self-Assessment.</p>
                </Card>
              </Panel>
              
              <Panel 
                header={
                  <span style={{ fontWeight: 'bold' }}>Understanding Progressive PAYE Calculations</span>
                } 
                key="2"
              >
                <Card size="small" bordered={false} style={{ background: '#f9f9f9' }}>
                  <p>
                    <strong>HMRC adjusts PAYE estimates throughout the tax year:</strong>
                  </p>
                  <ul>
                    <li><strong>Early in the tax year:</strong> HMRC has limited data about your annual income and may estimate based on current trends, potentially using a lower tax rate.</li>
                    <li><strong>Middle of the tax year:</strong> As more salary data becomes available, HMRC's projections become more accurate, possibly increasing the effective tax rate.</li>
                    <li><strong>Late in the tax year:</strong> HMRC has a clearer picture of your total annual income, leading to more precise tax calculations.</li>
                  </ul>
                  <p>This calculator estimates how PAYE might have appeared on your payslips at different points in the tax year, accounting for HMRC's improving income projections over time.</p>
                </Card>
              </Panel>
            </Collapse>
          </div>
        </>
      )}

      {/* Edit Vest Event Modal */}
      <Modal
        title="Edit RSU Vest Event"
        visible={isEditModalVisible}
        onOk={handleEditSave}
        onCancel={handleEditCancel}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item 
            name="date" 
            label="Vest Date" 
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item 
            name="amount" 
            label="Amount (£)" 
            rules={[{ required: true, message: 'Enter amount' }]}
          >
            <InputNumber 
              style={{ width: '100%' }}
              formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/£\s?|(,*)/g, '')}
              min={0}
            />
          </Form.Item>
          
          <Form.Item 
            name="withholding_rate" 
            label="Withholding Rate (%)"
          >
            <InputNumber 
              style={{ width: '100%' }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
              min={0}
              max={100}
            />
          </Form.Item>
          
          <Form.Item 
            name="employer_nic_rate" 
            label="Employer NIC Rate (%)"
          >
            <InputNumber 
              style={{ width: '100%' }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
              min={0}
              max={100}
            />
          </Form.Item>
          
          <Form.Item 
            name="sold_immediately" 
            valuePropName="checked"
          >
            <Checkbox checked={editingSoldImmediately} onChange={handleEditSoldImmediatelyChange}>
              Sold immediately
            </Checkbox>
          </Form.Item>
          
          <Form.Item 
            name="capital_gains" 
            label="Capital Gains (£)"
          >
            <InputNumber 
              style={{ width: '100%' }}
              formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/£\s?|(,*)/g, '')}
              min={0}
              disabled={editingSoldImmediately}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RsuVestInputs;